import {
  typeMatchesZoneSelection,
  ZONE_TYPE_TUPLE_SEPARATOR
} from '@alpinehomeair/common';
import { AirHandlerOptionsResponseType, CondenserType } from 'data/types';
import {
  hasCurrentAirHandlerPool,
  selectedTypeByZone
} from 'utils/air-handler-availability';
import {
  AirHandlerOptionsWithPoolType,
  AirHandlerPoolTupleAggregatesType,
  AirHandlerPoolTuplesType,
  FoldedPoolTuplesType,
  PerZoneTypes,
  ZoneTypeSelections
} from 'utils/air-handler-availability-types';

export const EMPTY_POOL_AGGREGATES: AirHandlerPoolTupleAggregatesType = {
  count: 0,
  minPrice: null,
  maxPrice: null,
  minSEER2: null,
  maxSEER2: null,
  minCondenser: null,
  maxCondenser: null
};

const minOf = (a: number | null, b: number | null): number | null => {
  if (a === null) return b;
  if (b === null) return a;
  return a < b ? a : b;
};

const maxOf = (a: number | null, b: number | null): number | null => {
  if (a === null) return b;
  if (b === null) return a;
  return a > b ? a : b;
};

const sumAggregates = (
  acc: AirHandlerPoolTupleAggregatesType,
  row: AirHandlerPoolTupleAggregatesType
): AirHandlerPoolTupleAggregatesType => ({
  count: acc.count + row.count,
  minPrice: minOf(acc.minPrice, row.minPrice),
  maxPrice: maxOf(acc.maxPrice, row.maxPrice),
  minSEER2: minOf(acc.minSEER2, row.minSEER2),
  maxSEER2: maxOf(acc.maxSEER2, row.maxSEER2),
  minCondenser: minOf(acc.minCondenser, row.minCondenser),
  maxCondenser: maxOf(acc.maxCondenser, row.maxCondenser)
});

export function getPoolTuples(
  options: AirHandlerOptionsResponseType | null | undefined
): AirHandlerPoolTuplesType | null {
  return (
    (options as AirHandlerOptionsWithPoolType | null | undefined)?.poolTuples ??
    null
  );
}

// Mirrors the API's fold-pool-tuples: a combination matches when every
// zone's part satisfies that zone's selection (unselected zones accept
// anything). `all` covers the whole pool, `matching` the compatible
// combinations, `perZoneTypes` the types each zone can still take.
export function foldPoolTuples(
  pool: AirHandlerPoolTuplesType,
  selections: ZoneTypeSelections
): FoldedPoolTuplesType {
  const perZoneSets = new Map<string, Set<string>>();
  let all = EMPTY_POOL_AGGREGATES;
  let matching = EMPTY_POOL_AGGREGATES;

  pool.rows.forEach((row) => {
    all = sumAggregates(all, row);
    const parts = row.tuple.split(ZONE_TYPE_TUPLE_SEPARATOR);
    const matches = pool.zones.every((zoneId, index) =>
      typeMatchesZoneSelection(parts[index] || null, selections[zoneId])
    );
    if (!matches) return;
    matching = sumAggregates(matching, row);
    pool.zones.forEach((zoneId, index) => {
      const typeCode = parts[index];
      if (!typeCode) return;
      const set = perZoneSets.get(zoneId) ?? new Set<string>();
      set.add(typeCode);
      perZoneSets.set(zoneId, set);
    });
  });

  const perZoneTypes: PerZoneTypes = {};
  perZoneSets.forEach((set, zoneId) => {
    perZoneTypes[zoneId] = [...set].sort();
  });
  return { all, matching, perZoneTypes };
}

// The fold for a condenser's current pool against its placed heads, or null
// when the pool is missing or stale (callers fall back to the server summary).
export function foldCondenserPool(
  condenser: Pick<CondenserType, 'airHandlerOptions' | 'zones'>
): FoldedPoolTuplesType | null {
  const pool = getPoolTuples(condenser.airHandlerOptions);
  if (!pool || !hasCurrentAirHandlerPool(condenser)) return null;
  return foldPoolTuples(pool, selectedTypeByZone(condenser));
}

export function matchingPoolSystemCount(
  condenser: Pick<CondenserType, 'airHandlerOptions' | 'zones'>
): number | null {
  const folded = foldCondenserPool(condenser);
  if (folded) return folded.matching.count;
  const summary = condenser.airHandlerOptions?.summary as
    | AirHandlerOptionsWithPoolType['summary']
    | undefined;
  return summary?.matchingCount ?? null;
}

export function totalPoolSystemCount(
  condenser: Pick<CondenserType, 'airHandlerOptions' | 'zones'>
): number | null {
  const folded = foldCondenserPool(condenser);
  if (folded) return folded.all.count;
  return condenser.airHandlerOptions?.summary.count ?? null;
}

type ValidSystemsPresenceCondenser = Pick<
  CondenserType,
  'airHandlerOptions' | 'zones' | 'validSystems' | 'validDuctedSystems'
>;

// How many systems a condenser can build: the current pool's count while
// the options own the step, otherwise the fetched rows (unique ids) plus the
// ducted list.
export function condenserValidSystemCount(
  condenser: ValidSystemsPresenceCondenser
): number {
  const folded = foldCondenserPool(condenser);
  if (folded) {
    return folded.all.count + (condenser.validDuctedSystems?.length ?? 0);
  }
  const uniqueIds = new Set((condenser.validSystems ?? []).map((s) => s.id));
  return uniqueIds.size + (condenser.validDuctedSystems?.length ?? 0);
}

export function condenserHasAnyValidSystem(
  condenser: ValidSystemsPresenceCondenser
): boolean {
  return condenserValidSystemCount(condenser) > 0;
}

// Matching systems for a condenser: the pool fold when current, otherwise
// the fetched rows' isMatchingAirHandlers flag.
export function condenserMatchingSystemCount(
  condenser: Pick<CondenserType, 'airHandlerOptions' | 'zones' | 'validSystems'>
): number {
  const folded = foldCondenserPool(condenser);
  if (folded) return folded.matching.count;
  return (condenser.validSystems ?? []).filter(
    (system) => system.isMatchingAirHandlers === true
  ).length;
}
